import { SendOutlined } from "@mui/icons-material";
import { LoadingButton } from "@mui/lab";
import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormLabel,
  Radio,
  RadioGroup,
  TextField,
  Typography,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import { enqueueSnackbar } from "notistack";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { EClientStatus } from "../../../shared/enums/client/EClient";
import { handleError } from "../../../shared/functions/handleError";
import { IClient } from "../../../shared/interfaces/client/IClient";
import {
  IVersion,
  TVersionNotificationLanguage,
} from "../../../shared/interfaces/version/IVersion";
import { ClientService } from "../../client/service";
import { VersionService } from "../service";

const LANGUAGES: TVersionNotificationLanguage[] = ["rs", "gb", "de"];

const recipientsOf = (client: IClient) =>
  client.notificationEmails?.length
    ? client.notificationEmails
    : [client.email];

interface IProps {
  version: IVersion | undefined;
  open: boolean;
  onClose: () => void;
  onSent: () => void;
}

const VersionNotifyModal = ({ version, open, onClose, onSent }: IProps) => {
  const { t } = useTranslation();
  const [clients, setClients] = useState<IClient[]>([]);
  const [selected, setSelected] = useState<IClient[]>([]);
  const [lang, setLang] = useState<TVersionNotificationLanguage>("rs");
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (!open) return;
    ClientService.get({ page: 1, perPage: 1000 })
      .then((response) => setClients(response?.data ?? []))
      .catch(handleError);
  }, [open]);

  const handleClose = () => {
    setSelected([]);
    setLang("rs");
    onClose();
  };

  const selectAllActive = () =>
    setSelected(
      clients.filter((client) => client.status === EClientStatus.ACTIVE),
    );

  const handleSend = () => {
    if (!version?.id || !selected.length) return;
    setIsLoading(true);
    VersionService.notify(version.id, {
      clientIds: selected.map((client) => client.id),
      lang,
    })
      .then((response) => {
        enqueueSnackbar(
          response.failed
            ? t("version.failedResult", {
                sent: response.sent,
                failed: response.failed,
              })
            : t("version.sentResult", { sent: response.sent }),
          { variant: response.failed ? "warning" : "success" },
        );
        onSent();
        handleClose();
      })
      .catch(handleError)
      .finally(() => setIsLoading(false));
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {t("version.notifyTitle", { version: version?.version })}
      </DialogTitle>
      <DialogContent>
        <Grid container spacing={2} sx={{ pt: 1 }}>
          <Grid size={{ xs: 12 }}>
            <Autocomplete
              multiple
              disableCloseOnSelect
              options={clients}
              value={selected}
              onChange={(_, value) => setSelected(value)}
              getOptionLabel={(client) => client.name || client.email}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              renderOption={(props, client, { selected: isSelected }) => (
                <li {...props} key={client.id}>
                  <Checkbox checked={isSelected} sx={{ mr: 1 }} />
                  <Box>
                    <Typography variant="body2">
                      {client.name || client.email}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {recipientsOf(client).join(", ")}
                    </Typography>
                  </Box>
                </li>
              )}
              renderInput={(params) => (
                <TextField {...params} label={t("version.selectClients")} />
              )}
            />
            <Button size="small" onClick={selectAllActive} sx={{ mt: 1 }}>
              {t("version.selectAllActive")}
            </Button>
          </Grid>
          <Grid size={{ xs: 12 }}>
            <FormControl>
              <FormLabel>{t("version.emailLanguage")}</FormLabel>
              <RadioGroup
                row
                value={lang}
                onChange={(e) =>
                  setLang(e.target.value as TVersionNotificationLanguage)
                }
              >
                {LANGUAGES.map((language) => (
                  <FormControlLabel
                    key={language}
                    value={language}
                    control={<Radio />}
                    label={t(`language.${language}`)}
                  />
                ))}
              </RadioGroup>
            </FormControl>
          </Grid>
          {selected.length > 0 && (
            <Grid size={{ xs: 12 }}>
              <FormLabel>{t("version.recipientsFor")}</FormLabel>
              {selected.map((client) => (
                <Typography key={client.id} variant="body2" sx={{ mt: 0.5 }}>
                  <b>{client.name || client.email}</b>:{" "}
                  {recipientsOf(client).join(", ")}
                </Typography>
              ))}
            </Grid>
          )}
          <Grid size={{ xs: 12 }}>
            <Typography variant="caption" color="text.secondary">
              {t("version.saveBeforeSending")}
            </Typography>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} color="secondary" variant="contained">
          {t("button.cancel")}
        </Button>
        <LoadingButton
          loading={isLoading}
          disabled={!selected.length}
          onClick={handleSend}
          variant="contained"
          startIcon={<SendOutlined />}
        >
          {t("button.send")}
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
};

export default VersionNotifyModal;
